"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  TRANSFER_CARD,
  TRANSFER_CARD_BODY_ONLY,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import type { TransferReportParams } from "@/modules/reports/presentation/api/client";
import { useTransferReportTrendQuery } from "@/modules/reports/presentation/api/queries";
import type {
  TransferReportDateField,
  TransferTrendGroupBy,
} from "@/modules/reports/domain/repo/transfer/transfer-report-filters";
import { Loader } from "@/shared/ui/Loader";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import type { TransferReportsSearch } from "./TransferReportsFilter";
import { TransferTrendDualAxisChart } from "./TransferTrendDualAxisChart";

const DATE_FIELD_LABEL: Record<TransferReportDateField, string> = {
  submitted: "ວັນທີສົ່ງຄຳຂໍ",
  shipped: "ວັນທີຈັດສົ່ງ",
  received: "ວັນທີຮັບ",
  closed: "ວັນທີປິດງານ",
};

const GROUP_BY_LABEL: Record<TransferTrendGroupBy, string> = {
  day: "ວັນ",
  week: "ອາທິດ",
  month: "ເດືອນ",
};

type Props = {
  reportParams: TransferReportParams;
};

function formatPeriodTick(
  value: string,
  groupBy: TransferTrendGroupBy,
): string {
  if (groupBy === "month") {
    const [year, month] = value.split("-");
    if (!year || !month) return value;
    return `${month}/${year.slice(2)}`;
  }
  if (groupBy === "week") {
    // period = ວັນເລີ່ມອາທິດ (date_trunc week)
    return value.length >= 10 ? value.slice(5) : value;
  }
  return value.length >= 10 ? value.slice(5) : value;
}

export function TransferReportsTrendChart({ reportParams }: Props) {
  const nav = useNavigate({ from: "/app/reports/inventory/transfers" });
  const search = useSearch({
    from: "/app/reports/inventory/transfers",
  }) as TransferReportsSearch;

  const groupBy = search.groupBy ?? "day";
  const trendParams = useMemo(
    () => ({ ...reportParams, groupBy }),
    [reportParams, groupBy],
  );

  const trend = useTransferReportTrendQuery(trendParams);
  const dateField = reportParams.dateField ?? "submitted";

  const chartData = useMemo(() => {
    return (trend.data ?? []).map((row) => ({
      period: row.period,
      requestCount: row.requestCount,
      qtyShipped: row.qtyShipped,
      shippedValueLak: row.shippedValueLak,
    }));
  }, [trend.data]);

  const setGroupBy = (next: TransferTrendGroupBy) => {
    nav({ search: { ...search, groupBy: next } });
  };

  return (
    <Card className={TRANSFER_CARD}>
      <CardContent className={TRANSFER_CARD_BODY_ONLY}>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="mb-1 font-semibold text-lg">ແນວໂນ້ມເບິກສະຕ໋ອກ</h3>
            <p className="text-muted-foreground text-sm">
              ຈັດກຸ່ມຕາມ{GROUP_BY_LABEL[groupBy]} · {DATE_FIELD_LABEL[dateField]}
              <span className="text-muted-foreground/80 mx-1">·</span>
              ຊ້າຍ: ຈຳນວນ · ຂວາ: ₭
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(["day", "week", "month"] as const).map((g) => (
              <Button
                key={g}
                type="button"
                variant={groupBy === g ? "default" : "outline"}
                size="sm"
                onClick={() => setGroupBy(g)}
              >
                ຕາມ{GROUP_BY_LABEL[g]}
              </Button>
            ))}
          </div>
        </div>
        {trend.isLoading ? (
          <div className="flex h-72 items-center justify-center">
            <Loader />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex h-72 items-center justify-center text-muted-foreground">
            ບໍ່ມີຂໍ້ມູນໃນຊ່ວງທີ່ເລືອກ
          </div>
        ) : (
          <div className="h-72 w-full min-w-0">
            <TransferTrendDualAxisChart
              data={chartData}
              config={{
                requestCount: {
                  label: "ຈຳນວນຄຳຂໍ",
                  color: "hsl(var(--chart-1))",
                },
                qtyShipped: {
                  label: "ຈຳນວນສົ່ງ (ລວມ)",
                  color: "hsl(var(--chart-2))",
                },
                shippedValueLak: {
                  label: "ມູນຄ່າສົ່ງ (₭)",
                  color: "hsl(var(--chart-3))",
                },
              }}
              xAxisTickFormatter={(value) => formatPeriodTick(value, groupBy)}
              className="h-full w-full"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
